import { RADIUS } from "@elyxeer/ui";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { CoverLibraryDialog } from "~/components/covers/CoverLibraryDialog";
import { MAX_GALLERY_IMAGES } from "~/types/news-admin";

/**
 * An article's photo gallery: up to ten photographs the member app lets a member
 * browse in the header, after the cover.
 *
 * The list lives in the form like every other field and is committed by the save.
 * Uploading only *publishes* a photo (into the cover library, 16:9 and watermarked
 * like a cover) and appends its URL here, so an editor can upload a batch, reorder
 * it, drop one, and still walk away without having changed the article.
 *
 * Several files can be chosen at once. They go up one at a time — ten 10 MB photos
 * in one request would outgrow the platform's request limit — and each appears as
 * soon as it is published. Files beyond the free slots are skipped, and said so.
 */
export function GalleryField({
  value,
  onChange,
  disabled = false,
  onUpload,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Resolves with the published URL, or null when the caller surfaced an error. */
  onUpload?: (
    file: File,
    options: { watermark: boolean },
  ) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [watermark, setWatermark] = useState(true);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // An i18n key and its count, resolved at render so it follows a language switch.
  const [notice, setNotice] = useState<{ key: string; count: number } | null>(
    null,
  );

  const free = MAX_GALLERY_IMAGES - value.length;
  const full = free <= 0;
  const locked = disabled || progress !== null;

  const move = (from: number, to: number) => {
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const add = (url: string) => {
    setNotice(null);
    if (value.includes(url)) {
      setNotice({ key: "gallery.alreadyIn", count: 0 });
      return;
    }
    if (full) return;
    onChange([...value, url]);
  };

  const pickFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset so choosing the same files again after a failure still fires onChange.
    event.target.value = "";
    if (!onUpload || files.length === 0) return;

    const accepted = files.slice(0, Math.max(free, 0));
    setNotice(
      files.length > accepted.length
        ? { key: "gallery.skipped", count: files.length - accepted.length }
        : null,
    );

    // Every control is locked while this runs, so nothing else can change the
    // list under the loop and the local copy stays the truth.
    let next = value;
    setProgress({ done: 0, total: accepted.length });
    for (const [index, file] of accepted.entries()) {
      const url = await onUpload(file, { watermark });
      // A failure is already on screen through the form's error; the rest still go up.
      if (url && !next.includes(url)) {
        next = [...next, url];
        onChange(next);
      }
      setProgress({ done: index + 1, total: accepted.length });
    }
    setProgress(null);
  };

  return (
    <Box
      data-testid="editor.gallery"
      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <FormLabel component="legend">{t("gallery.field")}</FormLabel>
        <Typography
          variant="T3"
          sx={{ color: "text.muted" }}
          data-testid="editor.gallery-count"
        >
          {value.length} / {MAX_GALLERY_IMAGES}
        </Typography>
      </Box>
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("gallery.help")}
      </Typography>

      {value.length > 0 ? (
        <Box
          component="ol"
          aria-label={t("gallery.field")}
          sx={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 1.5,
          }}
        >
          {value.map((url, index) => (
            <Box
              component="li"
              key={url}
              data-testid={`editor.gallery-item-${index}`}
              sx={{
                position: "relative",
                aspectRatio: "16 / 9",
                borderRadius: `${RADIUS.md}px`,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "surface.inset",
              }}
            >
              <Box
                component="img"
                src={url}
                alt=""
                loading="lazy"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  insetInline: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  padding: 0.5,
                  backgroundColor: "mediaScrim.main",
                  color: "mediaScrim.contrastText",
                }}
              >
                <Typography
                  variant="L3"
                  sx={{ paddingInline: 0.75, marginInlineEnd: "auto" }}
                >
                  {index + 1}
                </Typography>
                <GalleryAction
                  label={t("gallery.moveEarlier", { index: index + 1 })}
                  disabled={locked || index === 0}
                  onClick={() => move(index, index - 1)}
                  testId={`editor.gallery-earlier-${index}`}
                >
                  <ArrowBackRoundedIcon fontSize="small" />
                </GalleryAction>
                <GalleryAction
                  label={t("gallery.moveLater", { index: index + 1 })}
                  disabled={locked || index === value.length - 1}
                  onClick={() => move(index, index + 1)}
                  testId={`editor.gallery-later-${index}`}
                >
                  <ArrowForwardRoundedIcon fontSize="small" />
                </GalleryAction>
                <GalleryAction
                  label={t("gallery.remove", { index: index + 1 })}
                  disabled={locked}
                  onClick={() => {
                    setNotice(null);
                    onChange(value.filter((item) => item !== url));
                  }}
                  testId={`editor.gallery-remove-${index}`}
                >
                  <CloseRoundedIcon fontSize="small" />
                </GalleryAction>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryIcon />}
          disabled={locked || full}
          onClick={() => setLibraryOpen(true)}
          data-testid="editor.gallery-library"
        >
          {t("gallery.choose")}
        </Button>
        {onUpload ? (
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={locked || full}
            data-testid="editor.gallery-upload"
          >
            {t("gallery.upload")}
            <input
              type="file"
              hidden
              multiple
              // Mirrors the API's allow-list; anything else 422s anyway.
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => void pickFiles(event)}
              data-testid="editor.gallery-file"
            />
          </Button>
        ) : null}
        {progress ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("gallery.uploading", {
                done: progress.done,
                total: progress.total,
              })}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {onUpload ? (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={watermark}
              onChange={(event) => setWatermark(event.target.checked)}
              data-testid="editor.gallery-watermark"
            />
          }
          label={t("covers.watermark")}
        />
      ) : null}
      {full ? (
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          {t("gallery.full", { count: MAX_GALLERY_IMAGES })}
        </Typography>
      ) : null}
      {notice ? (
        <Alert severity="info" data-testid="editor.gallery-notice">
          {t(notice.key, { count: notice.count })}
        </Alert>
      ) : null}

      {/* Mounted only while open, so an editor that never opens it runs no query. */}
      {libraryOpen ? (
        <CoverLibraryDialog
          open
          currentUrl={null}
          onClose={() => setLibraryOpen(false)}
          onPick={add}
        />
      ) : null}
    </Box>
  );
}

function GalleryAction({
  label,
  disabled,
  onClick,
  testId,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <IconButton
      size="small"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      sx={{
        color: "inherit",
        "&.Mui-disabled": { color: "inherit", opacity: 0.35 },
      }}
    >
      {children}
    </IconButton>
  );
}
